/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button, Drawer, Spin, Modal, message } from 'antd';
import { BsCheck2All } from 'react-icons/bs';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleCheckBoldIco, Close } from '../Icons/IconMenu';
import { SettingIcon } from '../Icons/NavbarIcon';
import CommonJs from '@/utils/commonJs';
import { formatDateTimeUTC7 } from '@/utils/date';
import { NotificationSettingsDropdown } from './NotificationSettingsDropdown';
import { notifyApi } from '@/services/notify/notify-api';
import { useAuthStore } from '@/store/useAuthStore';
import { reportingApi } from '@/services/reporting/reporting-api';
import { GetModalPheDuyetBaoCao } from '@/components/common/ReportKPIsDetail/components/modalpheduyetbaocao/GetModalPheDuyetBaoCao';
import { buildNotificationHref, buildDuanNotificationHref, isDuanNotification, isNotificationRead, markAllNotificationsRead, sortNotificationsNewestFirst } from './notificationNavigation.mjs';
import { toSafeHtml } from '@/utils/sanitizeHtml'
interface Notification {
  id: string;
  message: string;
  timestamp: string;
  type: 'system' | 'kpi-report' | 'approval';
  isRead: boolean;
  avatarName?: string;
  // Raw data from API for advanced actions (e.g. KPI approval)
  raw?: any;
  phanHe?: string;
  idBaoCao?: string;
  approvalId?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onNotificationRead?: () => void;
}

type TabType = 'all' | 'process' | 'system' | 'kpi-report' | 'approval' | 'orther';
const NOTIFICATION_PAGE_SIZE = 10;
type NotificationCounter = Partial<Record<'Total' | 'System' | 'KpiReport' | 'Approval' | 'Orther', number>>;
type NotificationCounterPayload = NotificationCounter & {
  unread?: number;
  counter?: NotificationCounter;
};

export const NotifyDrawer: React.FC<Props> = ({ isOpen, onClose, onNotificationRead }) => {
  const { t } = useTranslation();
  const { profile } = useAuthStore();
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const [tabCounts, setTabCounts] = useState<Record<TabType, number>>({
    all: 0,
    process: 0,
    system: 0,
    'kpi-report': 0,
    approval: 0,
    orther: 0,
  });

  // State for KPI approval modal (getModalPheDuyetBaoCao)
  const [approveModalVisible, setApproveModalVisible] = useState(false);
  const [approveModalLoading, setApproveModalLoading] = useState(false);
  const [approveModalData, setApproveModalData] = useState<any[] | null>(null);
  const [approveModalTitle, setApproveModalTitle] = useState<string>('');
  const [lastApproveModalModel, setLastApproveModalModel] = useState<{ phanHe: string; id: string | null; IDNoti: string } | null>(null);
  // Map tabType to action for API
  const getActionByTabType = (tabType: TabType): string | null => {
    const actionMap: Record<TabType, string | null> = {
      all: null,
      process: 'quytrinh',
      system: 'system',
      'kpi-report': 'kpi-report',
      approval: 'approval',
      orther: 'orther',
    };
    return actionMap[tabType];
  };

  // Load notifications from API
  const loadNotifications = useCallback(async (tabType: TabType, page = 1, append = false) => {
    const toID = profile?.id;
    if (!toID || !isOpen) return;

    if (append) setLoadingMore(true);
    else {
      setLoading(true);
      setHasMore(false);
    }
    try {
      const action = getActionByTabType(tabType);
      const res: any = await notifyApi.list({ toID, action, pageNumber: page, pageSize: NOTIFICATION_PAGE_SIZE });

      const data = res?.data?.data ?? res?.data ?? res;
      const items = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : Array.isArray(data?.list) ? data.list : [];

      // Map API response to Notification interface
      const mappedNotifications: Notification[] = sortNotificationsNewestFirst(items).map((item: any) => {
        const phanHe = item.phanHe || item.PhanHe || null;
        const idBaoCao = item.idBaoCao || item.IDBaoCao || null;
        const approvalId = item.itemID
        return {
          id: item.id || item.ID || '',
          message: item.content || item.message || item.Content || item.Message || '',
          timestamp: item.date || item.timestamp || item.Date || item.Timestamp || '',
          type: (item.type || item.Type || item.action || item.Action || 'orther') as Notification['type'],
          isRead: isNotificationRead(item),
          avatarName: item.avatarName || item.fromName || item.FromName || item.senderName || 'System',
          raw: item,
          phanHe: phanHe || undefined,
          idBaoCao: idBaoCao || undefined,
          approvalId: approvalId || undefined,
        };
      });

      setNotifications((current) => append ? [...current, ...mappedNotifications] : mappedNotifications);
      setPageNumber(page);
      setHasMore(items.length === NOTIFICATION_PAGE_SIZE);
    } catch (err) {
      console.warn('loadNotifications failed:', err);
      if (!append) setNotifications([]);
      setHasMore(false);
    } finally {
      if (append) setLoadingMore(false);
      else setLoading(false);
    }
  }, [profile?.id, isOpen]);

  // Load notifications when tab changes or drawer opens
  useEffect(() => {
    if (isOpen) {
      void loadNotifications(activeTab);
    }
  }, [activeTab, isOpen, loadNotifications]);

  useEffect(() => {
    const root = listRef.current;
    const target = loadMoreRef.current;
    if (!root || !target || !hasMore || loading || loadingMore) return;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) void loadNotifications(activeTab, pageNumber + 1, true);
    }, { root, rootMargin: '160px' });
    observer.observe(target);
    return () => observer.disconnect();
  }, [activeTab, hasMore, loading, loadingMore, loadNotifications, pageNumber]);

  // Refetch modal data (getModalPheDuyetBaoCao) – used when opening modal and when noti-baobaokpi fires
  const loadApproveModalData = useCallback(async (model: { phanHe: string; id: string | null; IDNoti: string }): Promise<any[]> => {
    setApproveModalLoading(true);
    try {
      const resp: any = await reportingApi.getModalPheDuyetBaoCao(model as any);
      const data = resp?.data ?? resp;
      const list = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
      const result = list?.length ? list : [];
      setApproveModalData(result);
      const first = list[0] || {};
      const kpiName = first.tenKPI || first.tenkpi || first.TenKPI || '';
      setApproveModalTitle(
        kpiName
          ? t('bsc.title.pheduyetbaocaokpi_voiten', 'Phê duyệt báo cáo KPI: {{name}}', {
              defaultValue: 'Phê duyệt báo cáo KPI: {{name}}',
              name: kpiName,
            })
          : t('bsc.title.pheduyetbaocaokpi', 'Phê duyệt báo cáo KPI', { defaultValue: 'Phê duyệt báo cáo KPI' })
      );
      return result;
    } catch (err) {
      console.error('getModalPheDuyetBaoCao failed', err);
      message.error(t('bsc.msg.khongthetaidanhsachbaocaopheduyet', 'Không thể tải danh sách báo cáo cần phê duyệt'));
      setApproveModalData([]);
      return [];
    } finally {
      setApproveModalLoading(false);
    }
  }, [t]);

  useEffect(() => {
    const onRefreshModal = () => {
      if (approveModalVisible && lastApproveModalModel) {
        loadApproveModalData(lastApproveModalModel);
      }
    };
    window.addEventListener('noti-baobaokpi', onRefreshModal);
    return () => window.removeEventListener('noti-baobaokpi', onRefreshModal);
  }, [approveModalVisible, lastApproveModalModel, loadApproveModalData]);

  // Load counts for all tabs when drawer opens (only once)
  useEffect(() => {
    if (isOpen && profile?.id) {
      const loadTabCounts = async () => {
        try {
          const [res, processRes] = await Promise.all([
            notifyApi.counter({ toID: profile.id, action: null }),
            notifyApi.counter({ toID: profile.id, action: 'quytrinh' }),
          ]);
          const data = ((res as { data?: { data?: NotificationCounterPayload } }).data?.data
            ?? (res as { data?: NotificationCounterPayload }).data
            ?? res) as NotificationCounterPayload;
          const processData = ((processRes as { data?: { data?: NotificationCounterPayload } }).data?.data
            ?? (processRes as { data?: NotificationCounterPayload }).data
            ?? processRes) as NotificationCounterPayload;
          const counter = data?.counter ?? data ?? {};
          setTabCounts({
            all: Number(data?.unread ?? counter.Total ?? 0),
            process: Number(processData?.unread ?? 0),
            system: Number(counter.System ?? 0),
            'kpi-report': Number(counter.KpiReport ?? 0),
            approval: Number(counter.Approval ?? 0),
            orther: Number(counter.Orther ?? 0),
          });
        } catch (err) {
          console.warn('loadTabCounts failed:', err);
        }
      };

      loadTabCounts();
    }
  }, [isOpen, profile?.id]);

  const tabs: { key: TabType; label: string; count?: number }[] = [
    { key: 'all', label: t('vanhoa.select.tatca', 'Tất cả'), count: tabCounts.all },
    { key: 'process', label: 'Quy trình', count: tabCounts.process },
    { key: 'system', label: t('api.notification.type.system', 'Hệ thống'), count: tabCounts.system },
    { key: 'kpi-report', label: t('api.notification.type.baocao', 'Báo cáo KPIs'), count: tabCounts['kpi-report'] },
    { key: 'approval', label: t('api.notification.type.approve', 'Xét duyệt'), count: tabCounts.approval },
    { key: 'orther', label: t('bsc.label.khac', 'Khác'), count: tabCounts.orther },
  ];

  const filteredNotifications = notifications;

  const getInitialLetter = (name?: string) => {
    return name?.charAt(0)?.toUpperCase() || '?';
  };

  const markNotificationAsRead = async (notification: Notification) => {
    if (notification.isRead || !notification.id) return;
    try {
      const res: any = await notifyApi.setRead(notification.id);
      if (res?.success === true || res?.isSuccess === true) {
        setNotifications((current) => current.map((item) =>
          item.id === notification.id ? { ...item, isRead: true } : item,
        ));
        onNotificationRead?.();
      }
    } catch (err) {
      console.warn('set notification read failed:', err);
    }
  };

  const markAllNotificationsAsRead = async () => {
    const toID = profile?.id;
    if (!toID) return;
    try {
      const res: any = await notifyApi.setReadAll({ toID, action: null });
      if (res?.success === true || res?.isSuccess === true) {
        setNotifications(markAllNotificationsRead);
        setTabCounts({ all: 0, process: 0, system: 0, 'kpi-report': 0, approval: 0, orther: 0 });
        onNotificationRead?.();
        message.success('Đã đánh dấu tất cả thông báo là đã đọc');
      }
    } catch (err) {
      console.warn('set all notifications read failed:', err);
    }
  };

  // Handler: click thông báo
  const handleNotificationClick = async (notification: Notification) => {
    console.log('notification', notification);
    await markNotificationAsRead(notification);
    const raw = notification.raw || {};
    const rawType =
      raw.type ||
      raw.Type ||
      raw.action ||
      raw.Action ||
      notification.type;
    const typeStr = String(rawType || '').toLowerCase();
    const moduleName = String(raw.module || raw.Module || '').toLowerCase();
    const itemId = raw.itemID || raw.ItemID || notification.approvalId || '';
    const href = buildNotificationHref({
      action: raw.actionUrl || raw.ActionUrl || raw.action || raw.Action,
      itemId,
      module: moduleName,
      message: notification.message,
    });

    if (href?.includes('/quy-trinh/')) {
      onClose();
      window.location.href = href;
      return;
    }

    const duanHref = isDuanNotification({ module: moduleName, action: raw.actionUrl || raw.ActionUrl || raw.action || raw.Action })
      ? buildDuanNotificationHref({
        action: raw.actionUrl || raw.ActionUrl || raw.action || raw.Action,
        itemId,
      })
      : null;
    if (duanHref) {
      onClose();
      window.location.href = duanHref;
      return;
    }

    // Đồng bộ cơ cấu → trang notification
    if (moduleName === 'sync_cocau' || typeStr.includes('sync_cocau')) {
      const href = itemId
        ? `/thiet-lap/api-co-cau/thong-bao?runId=${encodeURIComponent(itemId)}`
        : '/thiet-lap/api-co-cau/thong-bao';
      onClose();
      if (typeof window !== 'undefined') {
        window.location.href = href;
      }
      return;
    }

    if (moduleName === 'chitieudoluong' || typeStr.includes('/buoc6')) {
      const href = `${typeStr}`;
      onClose();
      if (typeof window !== 'undefined') {
        window.location.href = href;
      }
      return;
    }

    // Phê duyệt báo cáo KPI
    if (!typeStr.includes('bao-cao-kpis')) return;
    const phanHe = notification.phanHe || raw.phanHe || raw.PhanHe || 'bsc';
    const idApproval = notification.approvalId || null;
    const IDNoti = notification.id;
    const model = { phanHe, id: idApproval, IDNoti };

    setApproveModalVisible(true);
    setApproveModalData(null);
    setLastApproveModalModel(model);

    try {
      const list = await loadApproveModalData(model);
      if (!list?.length) {
        message.info(t('bsc.msg.khongcobaocaocanpheduyet', 'Không có báo cáo nào cần phê duyệt.'));
      }
    } catch {
      setApproveModalData(null);
    }
  };

  return (
    <>
      <Drawer
        title={
          <div className="flex items-center justify-between w-full">
            <div className="text-1B252E font-bold text-[16px] leading-[39px]">{t('notify.title', 'Thông báo')}</div>
            <div className="flex items-center gap-2">
              <Button
                type="text"
                aria-label="mark-all-as-read"
                title="Đánh dấu tất cả là đã đọc"
                onClick={markAllNotificationsAsRead}
                className="text-gray-500 hover:text-gray-700 p-0 w-6 h-6 flex items-center justify-center"
                icon={<BsCheck2All size={22} aria-hidden="true" />}
              />
              <NotificationSettingsDropdown>
                <Button
                  type="text"
                  aria-label="settings"
                  className="text-gray-500 hover:text-gray-700 p-0 w-6 h-6 flex items-center justify-center"
                  icon={<SettingIcon />}
                />
              </NotificationSettingsDropdown>
              <Button
                type="text"
                aria-label="close"
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 p-0 w-6 h-6 flex items-center justify-center"
                icon={<Close />}
              />
            </div>
          </div>
        }
        open={isOpen}
        onClose={onClose}
        width={560}
        closable={false}
        styles={{
          mask: { backgroundColor: 'transparent' },
          body: { backgroundColor: '#F2F4F8', padding: 0 },
        }}
      >
        <div className="flex flex-col h-full">
        {/* ============== Tabs ============== */}
        <div className="flex gap-1 mb-4 border-b border-gray-200 px-5 py-6" style={{ overflow:'auto' }}>
            {tabs.map((tab) => {
              const isActive = activeTab === tab.key;
              return (
                <Button
                  type="default"
                  size="middle"
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`
                  flex justify-center items-center !gap-1 !px-[13px] !py-2 !rounded-2xl hover:!text-inherit hover:!border-inherit hover:!bg-[#E5E5E5]
                `}
                  style={{ backgroundColor: isActive ? '#E5E5E5' : 'transparent' }}
                >
                  {isActive && <CircleCheckBoldIco width={16} height={16} />}
                  <span>{tab.label}</span>
                  {tab.count !== undefined && (
                    <span className={`text-sm ${isActive ? '' : 'text-gray-500'}`}>({tab.count})</span>
                  )}
                </Button>
              );
            })}
          </div>

          {/* ============== Notifications List ============== */}
          <div ref={listRef} className="flex-1 overflow-y-auto px-5 pb-6">
            <Spin spinning={loading}>
              <div className="flex flex-col gap-2">
                {filteredNotifications.length === 0 && !loading ? (
                  <div className="text-center text-gray-500 py-8">Không có thông báo nào</div>
                ) : (
                  filteredNotifications.map((notification) => {
                    return (
                      <div
                        key={notification.id}
                        className="flex items-start gap-3 p-4 bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer relative"
                        onClick={() => handleNotificationClick(notification)}
                      >
                        {/* Avatar */}
                        <div
                          className="w-[48px] h-[48px] rounded-full p-[3px] flex-shrink-0"
                          style={{ background: 'linear-gradient(135deg, #FFAB00 0%, #0C68E9 100%)' }}
                        >
                          <div
                            className="w-full h-full rounded-full flex items-center justify-center text-white text-lg font-semibold"
                            style={{ backgroundColor: CommonJs.genColorByName(notification.avatarName || '') }}
                          >
                            {getInitialLetter(notification.avatarName)}
                          </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div
                            className="font-bold text-gray-800 leading-5 mb-1"
                            dangerouslySetInnerHTML={toSafeHtml(notification.message)}
                          />
                          <div className="text-sm text-gray-500">{formatDateTimeUTC7(notification.timestamp)}</div>
                        </div>

                        {/* Red dot indicator */}
                        {!notification.isRead && <div className="w-2 h-2 bg-[#DC2626] rounded-full flex-shrink-0" />}
                      </div>
                    );
                  })
                )}
                <div ref={loadMoreRef} className="h-px text-center">{loadingMore && <Spin size="small" />}</div>
              </div>
            </Spin>
          </div>
        </div>
      </Drawer>

      {/* Modal: danh sách báo cáo cần phê duyệt (getModalPheDuyetBaoCao) */}
      {!approveModalLoading && (
        <Modal
          open={approveModalVisible}
          title={approveModalTitle || 'Phê duyệt báo cáo KPI'}
          onCancel={() => {
            setApproveModalVisible(false);
            setApproveModalData(null);
            setLastApproveModalModel(null);
          }}
          footer={null}
          width={'100%'}
          destroyOnHidden
        >
          <GetModalPheDuyetBaoCao data={approveModalData || []} />
        </Modal>
      )}
    </>
  );
};
